/**
 * Allocation Weights Context
 * 
 * Controls how lead distribution is weighted between buyer quality
 * and delivery pressure. Bids are disabled for the MVP.
 * 
 * Rules:
 * - bidWeight + qualityWeight === 1 (bidWeight defaults to 0 for MVP)
 * - Values clamped between 0 and 1
 * - Only affects priority scoring, not eligibility
 */

import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
} from "react";

// --- Type Definitions ---

export interface AllocationWeights {
  bidWeight: number;     // 0 → 1 (disabled in MVP)
  qualityWeight: number; // 0 → 1
}

export type AllocationPreset = "revenue" | "balanced" | "quality";

export interface AllocationPresetConfig {
  id: AllocationPreset;
  name: string;
  bidWeight: number;
  qualityWeight: number;
  description: string;
}

// --- Preset Definitions ---

export const ALLOCATION_PRESETS: AllocationPresetConfig[] = [
  {
    id: "revenue",
    name: "Revenue-Focused",
    bidWeight: 0.0,
    qualityWeight: 1.0,
    description: "Quality-weighted selection (bids disabled for MVP).",
  },
  {
    id: "balanced",
    name: "Balanced",
    bidWeight: 0.0,
    qualityWeight: 1.0,
    description: "Default setting. Quality + pressure only.",
  },
  {
    id: "quality",
    name: "Quality-Focused",
    bidWeight: 0.0,
    qualityWeight: 1.0,
    description: "Quality-forward selection (bids disabled for MVP).",
  },
];

// --- Context State & Actions ---

interface AllocationWeightsState {
  weights: AllocationWeights;
  activePreset: AllocationPreset | null;
}

interface AllocationWeightsActions {
  /** Set custom weights (auto-clamps and normalizes) */
  setWeights: (bidWeight: number, qualityWeight: number) => void;
  
  /** Apply a preset configuration */
  applyPreset: (presetId: AllocationPreset) => void;
  
  /** Get current weights (for routing engine) */
  getWeights: () => AllocationWeights;
}

// --- Initial State ---

const initialState: AllocationWeightsState = {
  weights: {
    bidWeight: 0.0,
    qualityWeight: 1.0,
  },
  activePreset: "balanced",
};

// --- Context ---

const AllocationWeightsContext = createContext<{
  state: AllocationWeightsState;
  actions: AllocationWeightsActions;
} | undefined>(undefined);

// --- Provider ---

export const AllocationWeightsProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AllocationWeightsState>(initialState);

  const setWeights = useCallback((bidWeight: number, qualityWeight: number) => {
    // Clamp values between 0 and 1
    const clampedBid = Math.max(0, Math.min(1, bidWeight));
    const clampedQuality = Math.max(0, Math.min(1, qualityWeight));
    
    // Normalize to ensure they sum to 1
    const total = clampedBid + clampedQuality;
    const normalizedBid = total > 0 ? clampedBid / total : 0;
    const normalizedQuality = total > 0 ? clampedQuality / total : 1;
    
    // Check if matches a preset
    const matchingPreset = ALLOCATION_PRESETS.find(
      (p) => 
        Math.abs(p.bidWeight - normalizedBid) < 0.01 && 
        Math.abs(p.qualityWeight - normalizedQuality) < 0.01
    );
    
    setState({
      weights: {
        bidWeight: normalizedBid,
        qualityWeight: normalizedQuality,
      },
      activePreset: matchingPreset?.id ?? null,
    });
    
    if (import.meta.env.MODE !== "production") {
      console.log("[AllocationWeights] Updated:", {
        bidWeight: normalizedBid,
        qualityWeight: normalizedQuality,
        preset: matchingPreset?.id ?? "custom",
      });
    }
  }, []);

  const applyPreset = useCallback((presetId: AllocationPreset) => {
    const preset = ALLOCATION_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    
    setState({
      weights: {
        bidWeight: preset.bidWeight,
        qualityWeight: preset.qualityWeight,
      },
      activePreset: presetId,
    });
    
    if (import.meta.env.MODE !== "production") {
      console.log("[AllocationWeights] Preset applied:", presetId);
    }
  }, []);

  const getWeights = useCallback(() => {
    return { ...state.weights };
  }, [state.weights]);

  const actions: AllocationWeightsActions = {
    setWeights,
    applyPreset,
    getWeights,
  };

  return (
    <AllocationWeightsContext.Provider value={{ state, actions }}>
      {children}
    </AllocationWeightsContext.Provider>
  );
};

// --- Hook ---

export const useAllocationWeights = () => {
  const context = useContext(AllocationWeightsContext);
  if (context === undefined) {
    throw new Error("useAllocationWeights must be used within an AllocationWeightsProvider");
  }
  return context;
};

export default AllocationWeightsContext;
